import AllocationInstructionsEmailPreview from './AllocationInstructionsEmailPreview';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import useSendQuotaInstructionsEmail from '@hooks/data/customer-accounts/mutations/useSendQuotaInstructionsEmail';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {ProductAccessStates} from '@account/constants/products';
import {formatErrorMsg, getError} from '@account/utils/errors';
import {useCustomerAccount} from '@hooks/data/ims/useCustomerAccount';
import {useProductAccesses} from '@hooks/data/products/useProductAccesses';

const PLANET_DOMAIN = '@planet.com';

const fmtDate = d => {
  if (!d) return null;
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

// Mirror the email template's per-product cells (Product / Quota / Term /
// Status) from a CA-level product access.
const toPreviewProduct = pa => {
  const units = pa.quota_units === 'sqm' ? 'sqkm' : pa.quota_units;
  let quota = '—';
  if (pa.unlimited_quota) {
    quota = 'Unlimited';
  } else if (pa.quota_total !== null && pa.quota_total !== undefined) {
    quota = `${Number(pa.quota_total).toLocaleString()}${units ? ` ${units}` : ''}`;
  }

  const start = fmtDate(pa.term_start_date);
  const end = fmtDate(pa.term_end_date);
  const term = start && end ? `${start} – ${end}` : '—';

  let status = 'In progress';
  if (pa.state === ProductAccessStates.ACTIVE) status = 'Active';
  else if (pa.state === ProductAccessStates.PENDING) status = 'Scheduled';

  return {
    key: pa.id,
    name: pa.title || pa.name || 'Product',
    quota,
    term,
    status,
  };
};

// Tracks which CAs were emailed in this browser session so we can warn on a
// re-send. The backend has no last-sent record yet (follow-up ticket), so this
// is a best-effort guard against accidental duplicate customer emails; it does
// not cover other users or tabs.
const sessionSentAccountIds = new Set();

const parseCc = raw =>
  raw
    .split(/[,\s]+/)
    .map(addr => addr.trim())
    .filter(Boolean);

// Shared inner UI for sending the allocation-instructions email. Owns its own
// recipient/cc fields, actions, and success/error states so it can be dropped
// into a standalone Dialog (CA page, SFDC post-provision prompt) or an existing
// modal step (Add Product) without stacking two Dialogs.
const AllocationInstructionsEmailForm = ({customerAccountId, onDone}) => {
  const {data: account, isLoading: accountLoading} = useCustomerAccount(
    customerAccountId,
    {enabled: !!customerAccountId}
  );

  const admins = account?.admins ?? [];
  const primaryAdmin = admins[0];

  // CA-level products the email will summarize (active + scheduled), mapped to
  // the same cells the template renders.
  const {data: productsData, isLoading: productsLoading} = useProductAccesses(
    {
      customerAccountId,
      state: [ProductAccessStates.ACTIVE, ProductAccessStates.PENDING],
      limit: 100,
    },
    {enabled: !!customerAccountId}
  );
  const previewProducts = useMemo(
    () =>
      (productsData?.results ?? [])
        // The email summarizes CA-level products only (build_ca_quota_summary);
        // workspace-level allocations carry a workspace_id and are excluded.
        .filter(pa => !pa.workspace_id)
        .map(toPreviewProduct),
    [productsData]
  );

  const [recipient, setRecipient] = useState('');
  const [recipientTouched, setRecipientTouched] = useState(false);
  const [ccRaw, setCcRaw] = useState('');
  const [sentTo, setSentTo] = useState(null);

  // Seed the recipient from the CA admin once the account loads, unless the CSM
  // has already edited the field.
  const seededRecipient =
    recipientTouched || recipient ? recipient : (primaryAdmin?.email ?? '');

  const {mutate, error, isLoading, setError} = useSendQuotaInstructionsEmail();

  const cc = useMemo(() => parseCc(ccRaw), [ccRaw]);
  const ccInvalid = cc.some(
    addr => !addr.toLowerCase().endsWith(PLANET_DOMAIN)
  );

  const alreadySentThisSession = sessionSentAccountIds.has(customerAccountId);

  const normalizedError = error ? getError(error) : null;
  const errorMessage = (() => {
    if (!normalizedError) return null;
    if (normalizedError.statusCode === 400) {
      return 'No products to summarize, or no recipient — add a recipient and try again.';
    }
    if (normalizedError.statusCode === 502) {
      return 'Email delivery failed upstream. Try again.';
    }
    return formatErrorMsg(normalizedError);
  })();

  const handleSend = () => {
    setError(null);
    mutate(
      {
        accountId: customerAccountId,
        recipientEmail: seededRecipient.trim(),
        cc,
      },
      {
        onSuccess: () => {
          sessionSentAccountIds.add(customerAccountId);
          setSentTo(seededRecipient.trim());
        },
      }
    );
  };

  if (accountLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
        <CircularProgress />
      </Box>
    );
  }

  // Terminal success state — a distinct panel rather than an auto-close, so the
  // CSM sees exactly who was emailed.
  if (sentTo) {
    return (
      <Stack spacing={2}>
        <Alert severity="success">
          Allocation instructions sent to {sentTo}.
        </Alert>
        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button variant="contained" onClick={onDone}>
            Done
          </Button>
        </Box>
      </Stack>
    );
  }

  const sendDisabled =
    !seededRecipient.trim() || ccInvalid || isLoading || productsLoading;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Sends the customer a summary of their products and a link to allocate
        quota to their workspaces.
      </Typography>

      {alreadySentThisSession && (
        <Alert severity="warning">
          Allocation instructions were already sent for this account in this
          session. Sending again will email the customer a second time.
        </Alert>
      )}

      <AllocationInstructionsEmailPreview
        caName={account?.name}
        products={previewProducts}
        loading={productsLoading}
      />

      <TextField
        label="Recipient"
        value={seededRecipient}
        onChange={e => {
          setRecipientTouched(true);
          setRecipient(e.target.value);
        }}
        placeholder="Defaults to the account admin"
        helperText={
          !seededRecipient.trim()
            ? 'This account has no admin — enter a recipient.'
            : primaryAdmin && admins.length > 1
              ? `Account admins: ${admins.map(a => a.email).join(', ')}`
              : ' '
        }
        error={recipientTouched && !seededRecipient.trim()}
        fullWidth
        size="small"
      />

      <TextField
        label="CC (optional)"
        value={ccRaw}
        onChange={e => setCcRaw(e.target.value)}
        placeholder="teammate@planet.com"
        helperText={
          ccInvalid
            ? 'CC addresses must be @planet.com emails.'
            : 'Planet emails only. Separate multiple with commas.'
        }
        error={ccInvalid}
        fullWidth
        size="small"
      />

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}>
        <Button onClick={onDone} disabled={isLoading}>
          Skip
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sendDisabled}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Send allocation instructions
        </Button>
      </Box>
    </Stack>
  );
};

AllocationInstructionsEmailForm.propTypes = {
  customerAccountId: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default AllocationInstructionsEmailForm;
